import React, { useState, useMemo } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { useToastContext } from 'components/Toast';
import { useAppContext } from 'components/AppContext';
import * as api from 'utils/api';
import { useQuery } from '@tanstack/react-query';
import { REFUNDABLE_TRANSACTION_TYPES, FloorPlanPackage } from 'constants/common';

const DEFAULT_REFUND_EMAIL_CONTENT = `<div>
We have successfully sent you a refund for unsuccessful design. Thank you for using our services.
</div>`;

const getEmailPreview = (emailContent) => `<div>
Hi John,
</div>
<br>
${emailContent}
<br>
<div>
Best regards.
</div>
<div>
Auto Design Team
</div>`;

const getEmailTemplate = (emailContent) => `<div>
Hi {{username}},
</div>
<br>
${emailContent}
<br>
<div>
Best regards.
</div>
<div>
Auto Design Team
</div>`;

const RefundItemType = {
  TRANSACTION: 'transaction',
  PACKAGE: 'package',
};

export default function RefundFloorPlan({
  onClose,
  onDone,
  floorPlanId,
}) {
  const { data: transactionsRes } = useQuery({
    queryKey: ['floor-plan-transactions', floorPlanId],
    queryFn: () => api.getFloorPlanTransactions(floorPlanId),
    cacheTime: 0,
    staleTime: 0,
  });
  const transactions = useMemo(() => transactionsRes?.transactions || [], [transactionsRes]);
  const [selectedTransactions, setSelectedTransactions] = useState({});

  const { request } = useAppContext();
  const { toastError } = useToastContext();
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState('');
  const [bonus, setBonus] = useState(0);
  const [editorState, setEditorState] = useState(() => {
    const contentBlock = htmlToDraft(DEFAULT_REFUND_EMAIL_CONTENT);
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      return EditorState.createWithContent(contentState);
    }

    return null;
  },
  );

  const html = useMemo(() => draftToHtml(convertToRaw(editorState.getCurrentContent())), [editorState]);

  const refund = async () => {
    setSubmitting(true);

    const { success, error } = await request(api.refundFloorPlan(floorPlanId, {
      reason,
      emailTemplate: getEmailTemplate(html),
      bonus: Number.isNaN(parseInt(bonus)) ? 0 : parseInt(bonus),
      transactions: selectedTransactions,
    }));

    if (success) {
      onDone();
      setSubmitting(false);
      onClose();
    } else {
      toastError(error);
      setSubmitting(false);
    }
  };

  const refundItems = useMemo(() => {
    const items = [];

    for (const t of transactions) {
      const refundedPackages = Object.values(t.info.refundTransactions || {}).flat();

      if (REFUNDABLE_TRANSACTION_TYPES.includes(t.type)) {
        let status;

        if (!t.info.refundTransactions) {
          status = '';
        } else if (t.info.packages && refundedPackages.length < t.info.packages.length) {
          status = 'Refunded partially';
        } else {
          status = 'Refunded';
        }

        const ableToSelect = !t.info.packages && !t.info.refundTransactions;
        items.push({
          id: t.id,
          type: RefundItemType.TRANSACTION,
          transaction: t,
          ableToSelect,
          checked: !!selectedTransactions[t.id],
          price: t.quantity,
          status,
        });
      }

      if (t.info.packages) {
        for (const p of t.info.packages) {
          const isRefunded = refundedPackages?.includes(p);
          const ableToSelect = !isRefunded && p !== FloorPlanPackage.VIDEO && p !== FloorPlanPackage.CAD;
          items.push({
            id: `${t.id}-${p}`,
            type: RefundItemType.PACKAGE,
            package: p,
            transaction: t,
            price: t.info.packagePrice?.[p] || 0,
            ableToSelect,
            checked: !!selectedTransactions[t.id]?.includes(p),
            status: isRefunded ? 'Refunded' : '',
          });
        }
      }
    }

    return items;
  }, [transactions, selectedTransactions]);

  const allowRefund = useMemo(() => Object.values(selectedTransactions).flat().length > 0, [selectedTransactions]);

  return (
    <Modal
      show
      onHide={onClose}
      backdrop="static"
      id="modal-refund"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Refund</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table>
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th>Type</th>
              <th>Info</th>
              <th>Coins</th>
              <th>Status</th>
              <th>{' '}</th>
            </tr>
          </thead>
          <tbody>
            {refundItems
              .map(item => (
                <tr key={item.id}>
                  <td>{item.type === RefundItemType.TRANSACTION && item.transaction.id}</td>
                  <td>{item.type === RefundItemType.TRANSACTION && item.transaction.type}</td>
                  <td>{item.type === RefundItemType.TRANSACTION ? item.transaction.info.packages?.join(', ') : item.package}</td>
                  <td>{item.price}</td>
                  <td>{item.status}</td>
                  <td>
                    {item.ableToSelect && (
                    <Form.Check
                      type="checkbox"
                      checked={item.checked}
                      onChange={() => {
                        if (item.type === RefundItemType.TRANSACTION) {
                          if (item.checked) {
                            setSelectedTransactions({
                              ...selectedTransactions,
                              [item.transaction.id]: undefined,
                            });
                          } else {
                            setSelectedTransactions({
                              ...selectedTransactions,
                              [item.transaction.id]: [],
                            });
                          }
                        } else if (item.checked) {
                          setSelectedTransactions({
                            ...selectedTransactions,
                            [item.transaction.id]: selectedTransactions[item.transaction.id]?.filter(p => p !== item.package) || [],
                          });
                        } else {
                          setSelectedTransactions({
                            ...selectedTransactions,
                            [item.transaction.id]: [
                              ...(selectedTransactions[item.transaction.id] || []),
                              item.package,
                            ],
                          });
                        }
                      }}
                    />
                    )}
                  </td>
                </tr>
              ))}
          </tbody>

        </Table>
        <Form.Group className="form-group">
          <Form.Label>Refund reason (Internal purpose)</Form.Label>
          <Form.Control
            as="textarea"
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="form-group">
          <Form.Label>Bonus coins (beside refunded amount)</Form.Label>
          <Form.Control
            value={bonus}
            type="number"
            step={1}
            onChange={e => setBonus(e.target.value)}
          />
        </Form.Group>
        <Form.Group
          className="form-group"
          style={{
            marginTop: 16,
          }}
        >
          <Form.Label>Refund Email (Send to customer)</Form.Label>
          <div style={{
            border: '1px solid #ccc',
          }}
          >
            <Editor
              editorState={editorState}
              toolbarClassName="toolbarClassName"
              wrapperClassName="wrapperClassName"
              editorClassName="editorClassName"
              onEditorStateChange={(state) => setEditorState(state)}
            />
          </div>
        </Form.Group>
        <Form.Group
          className="form-group"
          style={{
            marginTop: 16,
          }}
        >
          <Form.Label>Email Preview</Form.Label>
          <div
            dangerouslySetInnerHTML={{
              __html: getEmailPreview(html),
            }}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          disabled={submitting || !allowRefund}
          variant="danger"
          onClick={refund}
        >
          Refund
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
